"use client";
import "./BGallery.scss";
import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Container, Grid, MenuItem, Select, Typography } from "@mui/material";
import GalleryService from "@/app/_services/GalleryService";
import Gallery from "./components/Gallery";
import { UtilityNoIndexHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

export default function page() {
  const [allShowImages, setAllShowImages] = useState<any>({});
  const [currentShowName, setCurrentShowName] = useState("");
  const [currentShowImages, setCurrentShowImages] = useState<any[]>([]);

  useEffect(() => {
    async function getAllShowsAndImages() {
      try {
        const res = await GalleryService.getState();
        const shows = sortByDate(Object.keys(res).filter((k) => k));

        setAllShowImages(res);
        if (shows.length > 0) {
          setCurrentShowName(shows[0]);
          setCurrentShowImages(res[shows[0]]);
        } else {
          setCurrentShowName("");
          setCurrentShowImages([]);
        }
      } catch (error) {
        console.error("Error fetching gallery state:", error);
        setAllShowImages({});
        setCurrentShowName("");
        setCurrentShowImages([]);
      }
    }
    getAllShowsAndImages();
  }, []);

  useEffect(() => {
    if (!currentShowName) {
      setCurrentShowImages([]);
      return;
    }
    setCurrentShowImages(allShowImages[currentShowName] ?? []);
  }, [currentShowName, allShowImages]);

  const showNames = sortByDate(Object.keys(allShowImages).filter((k) => k));

  return (
    <>
    <UtilityNoIndexHelmet title="Gallery | Underground Overground Comedy" path="/gallery" />
    <Grid container direction="column" flex={1} maxWidth="100%" sx={{ width: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
      {/* Hero section — black backdrop behind text */}
      <Box sx={{ backgroundColor: "#000", py: { xs: 2.5, md: 3 }, px: { xs: 2, md: 4 }, width: "100%", boxSizing: "border-box" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 0, md: 2 }, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: { xs: "0.08em", sm: "0.12em" },
              color: "rgba(255,255,255,0.7)",
              textTransform: "uppercase",
              mb: 1,
              lineHeight: 1.35,
              wordBreak: "break-word",
            }}
          >
            Get Photos
          </Typography>
          <Typography
            variant="h1"
            sx={{
              fontFamily: pageFont,
              fontWeight: 700,
              fontSize: { xs: "1.85rem", sm: "2.75rem", md: "3.25rem" },
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color: "#fff",
              mb: 2,
              wordBreak: "break-word",
            }}
          >
            Relive the Night
          </Typography>
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: { xs: "0.9375rem", sm: "1rem" },
              lineHeight: 1.7,
              color: "rgba(255,255,255,0.9)",
              maxWidth: { xs: "100%", sm: 560 },
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            If you were at one of our recent shows, check out the photos from the night. These will be up for the next few weeks, so grab your favorites below.
          </Typography>
        </Container>
      </Box>

      {/* Show selector + gallery */}
      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, pb: 4, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Container maxWidth="lg" sx={{ px: { xs: 0, md: 2 }, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <GalleryTitle
            showName={currentShowName}
            setShowName={setCurrentShowName}
            showNames={showNames}
          />
          {showNames.length === 0 && (
            <Typography
              sx={{
                fontFamily: pageFont,
                color: "rgba(255,255,255,0.75)",
                py: 2,
                fontSize: { xs: "0.9375rem", sm: "1rem" },
              }}
            >
              No photo albums are available right now. Check back after the next show.
            </Typography>
          )}
          <Grid container direction="column" flex={1} sx={{ overflow: "auto" }}>
            <Gallery showName={currentShowName} imageItems={currentShowImages} />
          </Grid>
        </Container>
      </Box>
    </Grid>
    </>
  );
}

function GalleryTitle(props: any) {
  const { showName, setShowName, showNames } = props;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 1, sm: 2 },
        mb: 2,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontFamily: pageFont,
          color: "rgba(255,255,255,0.9)",
          fontSize: { xs: "0.9375rem", sm: "1rem" },
          wordBreak: "break-word",
          flexShrink: 0,
        }}
      >
        🎥 Show:
      </Typography>
      <Select
        value={showName}
        size="small"
        fullWidth
        displayEmpty
        onChange={(e) => setShowName(e.target.value)}
        sx={{
          color: "#fff",
          fontFamily: pageFont,
          minWidth: 0,
          flex: { sm: "1 1 auto" },
          maxWidth: "100%",
          "& .MuiSelect-select": { py: 0.75, px: 1.5, textOverflow: "ellipsis", overflow: "hidden" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.4)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
          "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.8)" },
        }}
      >
        {showName &&
          showNames.map((name: string) => (
            <MenuItem key={name} value={name}>
              <Typography sx={{ fontFamily: pageFont, wordBreak: "break-word", whiteSpace: "normal" }}>
                {name}
              </Typography>
            </MenuItem>
          ))}
      </Select>
    </Box>
  );
}

const sortByDate = (showNames: string[]) => {
  const extractDate = (str: string) => {
    const dateMatch = str.match(/\((\d{2})-(\d{2})-(\d{2})\)$/);
    if (dateMatch) {
      const month = parseInt(dateMatch[1], 10) - 1;
      const day = parseInt(dateMatch[2], 10);
      const year = 2000 + parseInt(dateMatch[3], 10);
      return new Date(year, month, day);
    }
    return null;
  };

  return showNames.sort((a: any, b: any) => {
    const dateA: any = extractDate(a);
    const dateB: any = extractDate(b);

    if (dateA && dateB) {
      return dateB - dateA;
    }
    return 0;
  });
};
